// Last-resort fallback for the blog pages, used only if src/data/wpPosts.json
// is empty -- e.g. on a fresh clone where `npm run fetch:posts` has never
// been run. In normal operation the baked posts cover everything and nothing
// here is reached.
//
// Everything in this file exists to make a BlogsData.jsx entry
// indistinguishable from a baked wpPosts.json record, so BlogCardsRow and
// BlogDetail need no knowledge of where a post came from.
import blogsData from "./BlogsData";

// Kept local rather than imported: scripts/fetch-posts.mjs does the same job
// for the baked posts, but it runs in Node where the textarea trick below is
// unavailable, so the two cannot share an implementation.
function decodeHtmlEntities(str) {
  if (!str) return str;
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
}

function toPlainText(html, maxLength = 160) {
  const text = decodeHtmlEntities(
    html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
  );
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;
}

// BlogsData stores a post body as a {heading, subheading, points,
// description, image} block list, while BlogDetail renders `contentHtml`
// through dangerouslySetInnerHTML with the .wp-content theme. Emitting the
// same tags WordPress would (h2/h3/ul/p/img) means the fallback article is
// styled by that theme exactly like a live one.
function blocksToHtml(content = []) {
  return content
    .map((block) => {
      const parts = [];
      if (block.heading) parts.push(`<h2>${block.heading}</h2>`);
      if (block.subheading) parts.push(`<h3>${block.subheading}</h3>`);
      if (block.points?.length) {
        parts.push(
          `<ul>${block.points.map((p) => `<li>${p}</li>`).join("")}</ul>`
        );
      }
      if (block.description) parts.push(`<p>${block.description}</p>`);
      if (block.image) parts.push(`<img src="${block.image}" alt="" />`);
      return parts.join("");
    })
    .join("");
}

// BlogsData dates are display strings ("22 Apr 2026"); JSON-LD's
// datePublished wants an ISO timestamp. Guard the parse rather than emit
// "Invalid Date" into structured data.
function toIsoDate(displayDate) {
  const parsed = new Date(displayDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function mapStaticBlog(blog) {
  return {
    ...blog,
    metaDescription: toPlainText(blog.shortDescription),
    dateIso: toIsoDate(blog.date),
    contentHtml: blocksToHtml(blog.content),
  };
}

export function fallbackBlogs() {
  return blogsData.map(mapStaticBlog);
}

export function fallbackBlogBySlug(slug) {
  const match = blogsData.find((blog) => blog.slug === slug);
  return match ? mapStaticBlog(match) : null;
}
